import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { AUTH } from "@/constants/testIds";
import { toast } from "sonner";
import BrandLogo from "@/components/BrandLogo";
import LanguageSelector from "@/components/LanguageSelector";
import { motion } from "framer-motion";
import {
  ArrowRight,
  DeviceMobile,
  EnvelopeSimple,
  ArrowLeft,
  Info,
} from "@phosphor-icons/react";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";

export default function LoginPage() {
  const [tab, setTab] = useState("email");
  const { t } = useTranslation();

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex flex-col justify-between p-14 flex-1 relative overflow-hidden">
        <div className="lele-grain absolute inset-0 opacity-40" />
        <div className="relative z-10 flex items-start justify-between gap-4">
          <BrandLogo size={44} showWordmark withSubline />
          <LanguageSelector />
        </div>
        <div className="relative z-10 max-w-md">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8 rounded-2xl border border-rose-400/25 bg-rose-500/[0.05] px-5 py-4"
          >
            <div className="text-[11px] uppercase tracking-[0.3em] text-rose-300 mb-2">
              {t("auth.quoteLabel")}
            </div>
            <p className="text-neutral-100 text-[15px] leading-relaxed">
              {t("auth.quote")}
            </p>
          </motion.div>
          <div className="text-xs uppercase tracking-[0.3em] text-indigo-300 mb-4">
            {t("brand.tagline_line1")} {t("brand.tagline_line2")}
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-light leading-[1.1] tracking-tight">
            {t("auth.heroPrefix")}
            <span className="block text-indigo-400">
              {t("auth.heroAccent")}
            </span>
          </h1>
          <p className="mt-6 text-neutral-400 text-base leading-relaxed">
            {t("auth.heroBody")}
          </p>
          <Link
            to="/about"
            className="mt-6 inline-flex items-center gap-2 text-sm text-neutral-300 hover:text-neutral-100"
          >
            {t("auth.readStory")}
            <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
        <div className="relative z-10 text-[11px] uppercase tracking-[0.3em] text-neutral-600">
          LeLe · {t("brand.tagline_line2")}
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 md:p-12 relative">
        <div className="w-full max-w-md lele-card p-8 md:p-10 relative">
          <div className="lg:hidden flex items-center justify-between mb-6">
            <BrandLogo size={40} showWordmark />
            <LanguageSelector />
          </div>
          <div className="hidden lg:flex justify-end mb-2">
            <LanguageSelector />
          </div>
          <div className="lg:hidden mb-5 rounded-xl border border-rose-400/25 bg-rose-500/[0.05] px-4 py-3">
            <div className="text-[10px] uppercase tracking-[0.3em] text-rose-300 mb-1">
              {t("auth.quoteLabel")}
            </div>
            <p className="text-sm text-neutral-100 leading-snug">
              {t("auth.quote")}
            </p>
          </div>
          <div className="text-xs uppercase tracking-[0.3em] text-neutral-500 mb-2">
            {t("auth.signIn")}
          </div>
          <h2 className="font-heading text-3xl mb-6">{t("auth.welcomeBack")}</h2>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 p-1 rounded-full border border-white/10 bg-white/[0.02]">
            <TabButton
              testId={AUTH.tabEmail}
              active={tab === "email"}
              onClick={() => setTab("email")}
              icon={<EnvelopeSimple size={14} weight="bold" />}
              label={t("auth.emailTab")}
            />
            <TabButton
              testId={AUTH.tabMobile}
              active={tab === "mobile"}
              onClick={() => setTab("mobile")}
              icon={<DeviceMobile size={14} weight="bold" />}
              label={t("auth.otpTab")}
            />
          </div>

          {tab === "email" ? <EmailForm /> : <MobileForm />}

          <div className="mt-6 text-center text-sm text-neutral-500">
            {t("auth.newToLele")}{" "}
            <Link
              data-testid={AUTH.loginGoToRegister}
              to="/register"
              className="text-indigo-400 hover:text-indigo-300"
            >
              {t("auth.createAccount")}
            </Link>
          </div>
          <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-center gap-4 text-[11px] uppercase tracking-[0.25em] text-neutral-600">
            <Link to="/about" className="hover:text-neutral-300">{t("header.about")}</Link>
            <span>·</span>
            <Link to="/terms" className="hover:text-neutral-300">Terms</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function TabButton({ testId, active, onClick, icon, label }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      type="button"
      className={`flex-1 flex items-center justify-center gap-2 text-xs font-medium py-2.5 rounded-full transition-colors ${
        active
          ? "bg-indigo-500/15 text-indigo-200 border border-indigo-400/40"
          : "text-neutral-400 hover:text-neutral-100"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

function EmailForm() {
  const { login } = useAuth();
  const nav = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, pw);
      toast.success(t("auth.welcomeBack"));
      nav("/");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <label className="block text-xs uppercase tracking-[0.2em] text-neutral-500 mb-2">
        {t("common.email")}
      </label>
      <input
        data-testid={AUTH.loginEmail}
        type="email"
        required
        autoComplete="email"
        className="lele-input w-full rounded-lg px-4 py-3 mb-5"
        placeholder={t("auth.emailPlaceholder")}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <label className="block text-xs uppercase tracking-[0.2em] text-neutral-500 mb-2">
        {t("common.password")}
      </label>
      <input
        data-testid={AUTH.loginPassword}
        type="password"
        required
        autoComplete="current-password"
        className="lele-input w-full rounded-lg px-4 py-3 mb-8"
        placeholder={t("auth.passwordPlaceholder")}
        value={pw}
        onChange={(e) => setPw(e.target.value)}
      />
      <button
        data-testid={AUTH.loginSubmit}
        type="submit"
        disabled={loading}
        className="lele-btn-primary w-full flex items-center justify-center gap-2"
      >
        {loading ? t("common.loading") : t("auth.signIn")}
        {!loading && <ArrowRight size={16} weight="bold" />}
      </button>
    </form>
  );
}

function MobileForm() {
  const { sendOtp, loginWithOtp } = useAuth();
  const nav = useNavigate();
  const { t } = useTranslation();
  const [step, setStep] = useState("phone"); // "phone" | "code"
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const send = async (e) => {
    if (e) e.preventDefault();
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 10) {
      toast.error("Enter a valid 10-digit mobile number");
      return;
    }
    setSending(true);
    try {
      const data = await sendOtp(phone);
      setStep("code");
      if (data?.dev_code) {
        setDevCode(data.dev_code);
        toast.success(`OTP sent (dev mode: ${data.dev_code})`);
      } else {
        toast.success("OTP sent to your phone");
      }
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSending(false);
    }
  };

  const verify = async (fullCode) => {
    const c = (fullCode ?? code).trim();
    if (c.length !== 6) {
      toast.error("Enter the 6-digit code");
      return;
    }
    setVerifying(true);
    try {
      await loginWithOtp({ phone, code: c, name: name.trim() });
      toast.success("Welcome to LeLe");
      nav("/");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setVerifying(false);
    }
  };

  const onCodeChange = (v) => {
    setCode(v);
    if (v.length === 6) verify(v);
  };

  if (step === "phone") {
    return (
      <form onSubmit={send}>
        <label className="block text-xs uppercase tracking-[0.2em] text-neutral-500 mb-2">
          {t("common.phone")}
        </label>
        <div className="flex gap-2 mb-5">
          <div className="lele-input rounded-lg px-3 py-3 text-sm text-neutral-300 flex items-center">
            +91
          </div>
          <input
            data-testid={AUTH.otpPhoneInput}
            type="tel"
            required
            autoComplete="tel"
            inputMode="numeric"
            className="lele-input flex-1 rounded-lg px-4 py-3"
            placeholder={t("auth.phonePlaceholder")}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </div>

        <label className="block text-xs uppercase tracking-[0.2em] text-neutral-500 mb-2">
          {t("auth.nameLabel")}
        </label>
        <input
          data-testid={AUTH.otpNameInput}
          type="text"
          className="lele-input w-full rounded-lg px-4 py-3 mb-8"
          placeholder={t("auth.namePlaceholder")}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        <button
          data-testid={AUTH.otpSendBtn}
          type="submit"
          disabled={sending}
          className="lele-btn-primary w-full flex items-center justify-center gap-2"
        >
          {sending ? t("common.sending") : t("auth.sendOtp")}
          {!sending && <ArrowRight size={16} weight="bold" />}
        </button>

        <div className="mt-4 text-[11px] text-neutral-500 flex items-start gap-2 leading-relaxed">
          <Info size={14} weight="duotone" className="text-indigo-400 mt-0.5 shrink-0" />
          <span>{t("onboarding.mobile.devOtpHint")}</span>
        </div>
      </form>
    );
  }

  // Step: code
  return (
    <div>
      <button
        data-testid={AUTH.otpChangePhoneBtn}
        onClick={() => {
          setStep("phone");
          setCode("");
          setDevCode("");
        }}
        className="text-xs text-neutral-500 hover:text-neutral-100 flex items-center gap-1 mb-4"
        type="button"
      >
        <ArrowLeft size={12} weight="bold" /> {t("onboarding.mobile.changeNumber")}
      </button>

      <div className="text-sm text-neutral-300 mb-1">
        {t("onboarding.mobile.sentTo", { phone: `+91 ${phone.replace(/\D/g, "")}` })}
      </div>
      <div className="text-[11px] text-neutral-500 mb-6">
        {t("onboarding.mobile.enterCode")}
      </div>

      {devCode && (
        <div className="mb-5 rounded-xl border border-indigo-400/30 bg-indigo-500/[0.06] px-4 py-3">
          <div className="text-[10px] uppercase tracking-[0.25em] text-indigo-300 mb-1">
            {t("onboarding.mobile.devOtp")}
          </div>
          <div className="font-mono text-xl tracking-[0.4em] text-indigo-200">
            {devCode}
          </div>
        </div>
      )}

      <div className="flex justify-center mb-6">
        <InputOTP
          data-testid={AUTH.otpCodeInput}
          maxLength={6}
          value={code}
          onChange={onCodeChange}
          containerClassName="gap-2"
        >
          <InputOTPGroup className="gap-2">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <InputOTPSlot
                key={i}
                index={i}
                className="!h-12 !w-11 !border !rounded-lg lele-input text-lg font-heading text-neutral-100"
              />
            ))}
          </InputOTPGroup>
        </InputOTP>
      </div>

      <button
        data-testid={AUTH.otpVerifyBtn}
        onClick={() => verify()}
        disabled={verifying || code.length !== 6}
        className="lele-btn-primary w-full flex items-center justify-center gap-2"
      >
        {verifying ? t("common.verifying") : t("auth.verifyOtp")}
        {!verifying && <ArrowRight size={16} weight="bold" />}
      </button>

      <button
        data-testid={AUTH.otpResendBtn}
        onClick={send}
        disabled={sending}
        type="button"
        className="mt-4 w-full text-xs text-neutral-500 hover:text-neutral-100"
      >
        {sending ? t("common.sending") : t("auth.sendOtp")}
      </button>
    </div>
  );
}
